import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { chequeHistoryImportService } from '../../../services/cheques/chequeHistoryImportService';
import { chequeService } from '../../../services/cheques/chequeService';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadArea = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragOver ? theme.palette.primary.light + '10' : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  }
}));

const ChequeHistoryImportDialog = ({ open, onClose, onImportComplete, banks, beneficiaries }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importProgressMessage, setImportProgressMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleClose = () => {
    if (isProcessing || isImporting) return;
    setActiveStep(0);
    setSelectedFile(null);
    setValidationResults(null);
    setImportResults(null);
    setError(null);
    setImportProgress(0);
    setImportProgressMessage('');
    onClose();
  };

  const handleDownloadTemplate = () => {
    try {
      chequeHistoryImportService.downloadTemplate();
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setSelectedFile(file);
      setError(null);
      setValidationResults(null);
      setImportResults(null);
      setActiveStep(0);
    } else {
      setError('Please select a valid Excel file (.xlsx)');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleValidate = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    setActiveStep(1);

    try {
      // Step 1: Validate file structure
      const fileData = await chequeHistoryImportService.validateFileStructure(selectedFile);

      // Check record limit
      if (fileData.rowCount > 5000) {
        setError(`File contains ${fileData.rowCount.toLocaleString()} rows. Maximum allowed is 5,000 rows per file. Please split your data into smaller files.`);
        setActiveStep(0);
        setIsProcessing(false);
        return;
      }

      // Step 2: Parse and validate rows client-side
      const results = chequeHistoryImportService.parseAndValidateRows(fileData.data, banks);

      // Step 3: Check duplicates against DB for valid rows
      const validRows = results.filter(r => r.isValid);
      if (validRows.length > 0) {
        const chequeNumbers = validRows.map(r => ({
          BankAccountName: r.parsed.bankAccountName,
          ChequeNumber: r.parsed.chequeNumber
        }));

        const dbResult = await chequeService.bulkValidateChequeNumbers(chequeNumbers);
        if (dbResult.success && dbResult.data) {
          const duplicates = dbResult.data.Duplicates || dbResult.data.duplicates || dbResult.data || [];
          if (Array.isArray(duplicates) && duplicates.length > 0) {
            // Mark rows with DB duplicates as invalid
            duplicates.forEach(dup => {
              const dupBankName = (dup.BankAccountName || dup.bankAccountName || '').toLowerCase();
              const dupChequeNum = dup.ChequeNumber || dup.chequeNumber;
              results.forEach(r => {
                if (
                  r.isValid &&
                  r.parsed.bankAccountName.toLowerCase() === dupBankName &&
                  r.parsed.chequeNumber === dupChequeNum
                ) {
                  r.isValid = false;
                  const existingStatus = dup.ExistingStatus || dup.existingStatus || '';
                  r.errors.push(`Cheque number already exists in database${existingStatus ? ` (Status: ${existingStatus})` : ''}`);
                }
              });
            });
          }
        }
      }

      setValidationResults(results);
      setActiveStep(2);
    } catch (err) {
      setError(err.message);
      setActiveStep(0);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!validationResults) return;

    const validRows = validationResults.filter(r => r.isValid);
    if (validRows.length === 0) return;

    setIsImporting(true);
    setImportProgress(0);
    setImportProgressMessage('Preparing import...');
    setActiveStep(3);
    setError(null);

    try {
      const cheques = validRows.map(r => ({
        BankAccountName: r.parsed.bankAccountName,
        ChequeNumber: r.parsed.chequeNumber,
        BeneficiaryName: r.parsed.beneficiaryName,
        ChqAmount: r.parsed.chqAmount,
        ChqDate: r.parsed.chqDate,
        Status: r.parsed.status,
        ClearedDate: r.parsed.clearedDate || null,
        Remarks: r.parsed.remarks
      }));

      const result = await chequeService.importHistoryChequesBatched(cheques, (progress) => {
        setImportProgress(progress.percent);
        setImportProgressMessage(
          `Processing batch ${progress.currentBatch} of ${progress.totalBatches} (${progress.processedSoFar} of ${progress.totalRecords} records)...`
        );
      });

      if (result.success) {
        const importData = result.data;
        const serverResults = importData.Results || importData.results || [];

        // Merge server results back into validation results
        const finalResults = validationResults.map(vr => {
          if (!vr.isValid) {
            return { ...vr, importStatus: 'SKIPPED', importMessage: vr.errors.join('; ') };
          }

          // Find matching server result
          const serverResult = serverResults.find(sr =>
            (sr.ChequeNumber || sr.chequeNumber) === vr.parsed.chequeNumber &&
            (sr.RowNumber || sr.rowNumber) === vr.rowNumber
          );

          if (serverResult) {
            const status = serverResult.Status || serverResult.status;
            const message = serverResult.Message || serverResult.message || '';
            return {
              ...vr,
              importStatus: status === 'SUCCESS' ? 'SUCCESS' : 'FAILED',
              importMessage: message
            };
          }

          return { ...vr, importStatus: 'SUCCESS', importMessage: '' };
        });

        setImportResults({
          totalProcessed: importData.TotalProcessed || importData.totalProcessed || 0,
          successCount: importData.SuccessCount || importData.successCount || 0,
          failedCount: importData.FailedCount || importData.failedCount || 0,
          beneficiariesCreated: importData.BeneficiariesCreated || importData.beneficiariesCreated || 0,
          results: finalResults
        });
      } else {
        setError(result.message || 'Import failed. Please try again.');
        setActiveStep(2);
      }

      setImportProgress(100);
      setImportProgressMessage('Import complete!');
    } catch (err) {
      setError('Import failed: ' + (err.message || 'Unknown error'));
      setActiveStep(2);
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadValidationReport = () => {
    if (validationResults) {
      chequeHistoryImportService.downloadResultFile(validationResults);
    }
  };

  const handleDownloadImportReport = () => {
    if (importResults?.results) {
      chequeHistoryImportService.downloadResultFile(importResults.results);
    }
  };

  const validCount = validationResults ? validationResults.filter(r => r.isValid).length : 0;
  const invalidCount = validationResults ? validationResults.filter(r => !r.isValid).length : 0;

  // Bank breakdown for review step
  const bankBreakdown = useMemo(() => {
    if (!validationResults) return [];
    const breakdown = {};
    validationResults.filter(r => r.isValid).forEach(r => {
      const bankName = r.parsed.bankName || r.parsed.bankAccountName;
      if (!breakdown[bankName]) {
        breakdown[bankName] = { bankName, count: 0, totalAmount: 0, currency: r.parsed.currency };
      }
      breakdown[bankName].count++;
      breakdown[bankName].totalAmount += r.parsed.chqAmount;
    });
    return Object.values(breakdown);
  }, [validationResults]);

  // New beneficiaries count
  const newBeneficiariesCount = useMemo(() => {
    if (!validationResults || !beneficiaries) return 0;
    const existingNames = new Set(beneficiaries.map(b => (b.beneficiaryName || b.BeneficiaryName || '').toLowerCase()));
    const importNames = new Set(
      validationResults
        .filter(r => r.isValid)
        .map(r => r.parsed.beneficiaryName.toLowerCase())
    );
    let count = 0;
    importNames.forEach(name => {
      if (!existingNames.has(name)) count++;
    });
    return count;
  }, [validationResults, beneficiaries]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '600px' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Import Cheque History
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={isProcessing || isImporting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Template Info Card */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FileIcon color="primary" />
              <Typography variant="h6">Import Historical Cheque Data</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import cheques previously printed from other software or manual records.
              Download the template, fill it with your data, and upload to import.
            </Typography>
            <List dense sx={{ mb: 2 }}>
              <ListItem>
                <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Required Fields"
                  secondary="Bank Account Name, Cheque Number, Beneficiary Name, Amount, Cheque Date, Status"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Conditional Fields"
                  secondary="Cleared Date (required when Status is Cleared)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Multi-Bank Support"
                  secondary="You can include cheques from multiple banks in a single file (up to 5,000 rows)"
                />
              </ListItem>
            </List>
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              size="small"
            >
              Download Template
            </EasyCheckButton>
          </CardContent>
        </Card>

        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 0: Upload */}
          <Step>
            <StepLabel>Upload File</StepLabel>
            <StepContent>
              <UploadArea
                isDragOver={isDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Upload Excel File'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drag and drop your Excel file here, or click to browse
                </Typography>
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                />
              </UploadArea>

              {selectedFile && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FileIcon color="success" />
                      <Box>
                        <Typography variant="subtitle2">{selectedFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <EasyCheckButton
                buttonType="action"
                variant="contained"
                onClick={handleValidate}
                disabled={!selectedFile}
                loading={isProcessing}
                startIcon={<UploadIcon />}
                sx={{ mt: 1 }}
              >
                Validate & Preview
              </EasyCheckButton>
            </StepContent>
          </Step>

          {/* Step 1: Validating */}
          <Step>
            <StepLabel>Validating</StepLabel>
            <StepContent>
              {isProcessing && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Validating rows...</Typography>
                    <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Validating fields and checking for duplicate cheque numbers...
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </StepContent>
          </Step>

          {/* Step 2: Review */}
          <Step>
            <StepLabel>Review & Confirm</StepLabel>
            <StepContent>
              {validationResults && (
                <Box>
                  {/* Summary Cards */}
                  <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" fontWeight={600}>
                          {validationResults.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Rows</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" fontWeight={600}>
                          {validCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Valid</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main" fontWeight={600}>
                          {invalidCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Invalid</Typography>
                      </CardContent>
                    </Card>
                  </Stack>

                  {/* Bank Breakdown */}
                  {bankBreakdown.length > 0 && (
                    <Card sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <BankIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle1" fontWeight={600}>Bank Breakdown (Valid Rows)</Typography>
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Bank Account</TableCell>
                                <TableCell align="center">Cheques</TableCell>
                                <TableCell align="right">Total Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {bankBreakdown.map((bank) => (
                                <TableRow key={bank.bankName}>
                                  <TableCell>{bank.bankName}</TableCell>
                                  <TableCell align="center">{bank.count}</TableCell>
                                  <TableCell align="right">
                                    {bank.currency} {formatCurrencyAmount(bank.totalAmount, bank.currency, { withSymbol: false })}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* New Beneficiaries Info */}
                  {newBeneficiariesCount > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {newBeneficiariesCount} new {newBeneficiariesCount === 1 ? 'beneficiary' : 'beneficiaries'} will be automatically created during import.
                    </Alert>
                  )}

                  {/* Row Details Table */}
                  <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Bank</TableCell>
                          <TableCell>Cheque #</TableCell>
                          <TableCell>Beneficiary</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Valid</TableCell>
                          <TableCell>Errors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResults.map((result) => (
                          <TableRow key={result.rowNumber}>
                            <TableCell>{result.rowNumber}</TableCell>
                            <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {result.data['Bank Account Name']}
                            </TableCell>
                            <TableCell>{result.data['Cheque Number'] || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {result.data['Beneficiary Name']}
                            </TableCell>
                            <TableCell align="right">{result.data['Amount']}</TableCell>
                            <TableCell>{result.data['Cheque Date']}</TableCell>
                            <TableCell>{result.data['Status']}</TableCell>
                            <TableCell>
                              <Chip
                                icon={result.isValid ? <SuccessIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                label={result.isValid ? 'VALID' : 'INVALID'}
                                color={result.isValid ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="error.main">
                                {result.errors.join('; ')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <EasyCheckButton
                      buttonType="action"
                      variant="contained"
                      onClick={handleImport}
                      disabled={validCount === 0}
                    >
                      Import {validCount} Valid Cheques
                    </EasyCheckButton>
                    <EasyCheckButton
                      buttonType="action"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadValidationReport}
                    >
                      Download Validation Report
                    </EasyCheckButton>
                  </Box>
                </Box>
              )}
            </StepContent>
          </Step>

          {/* Step 3: Importing */}
          <Step>
            <StepLabel>Import Complete</StepLabel>
            <StepContent>
              {isImporting && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Importing cheques...</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={importProgress}
                      sx={{ height: 8, borderRadius: 4, mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {importProgressMessage || 'Please wait while your cheques are being imported...'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Please do not close this dialog. Large imports may take a few minutes.
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {importResults && (
                <Box>
                  <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" fontWeight={600}>
                          {importResults.totalProcessed}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Processed</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" fontWeight={600}>
                          {importResults.successCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Imported</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main" fontWeight={600}>
                          {importResults.failedCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Failed</Typography>
                      </CardContent>
                    </Card>
                    {importResults.beneficiariesCreated > 0 && (
                      <Card sx={{ flex: 1 }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main" fontWeight={600}>
                            {importResults.beneficiariesCreated}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">New Beneficiaries</Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Stack>

                  {importResults.successCount > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Successfully imported {importResults.successCount} cheque{importResults.successCount !== 1 ? 's' : ''}.
                    </Alert>
                  )}

                  {importResults.failedCount > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {importResults.failedCount} cheque{importResults.failedCount !== 1 ? 's' : ''} failed to import. Download the report for details.
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <EasyCheckButton
                      buttonType="action"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadImportReport}
                    >
                      Download Import Report
                    </EasyCheckButton>
                    <EasyCheckButton
                      buttonType="action"
                      variant="contained"
                      onClick={() => {
                        onImportComplete();
                        handleClose();
                      }}
                    >
                      Done
                    </EasyCheckButton>
                  </Box>
                </Box>
              )}
            </StepContent>
          </Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <EasyCheckButton
          buttonType="form"
          variant="outlined"
          onClick={handleClose}
          disabled={isProcessing || isImporting}
        >
          {importResults ? 'Close' : 'Cancel'}
        </EasyCheckButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChequeHistoryImportDialog;
